import { NavigationType, createPath, parsePath } from 'react-router-dom';
import type { Location, To } from 'react-router-dom';

/**
 * A browser history for `unstable_HistoryRouter` that keeps the address bar on
 * the site root. The real route (repertoire, node, `?path=…&ply=…`) lives in
 * `window.history.state`, so back/forward and reload restore it exactly as the
 * plain `BrowserRouter` did — only the visible URL is masked.
 *
 * Deep links still resolve: a first load of `/repertoires/…` is read from the
 * real URL and then masked in place. `createHref` returns the real route, so
 * link hover previews and "open in new tab" keep working.
 */

type Action = NavigationType;
type Listener = (update: { action: Action; location: Location; delta: number | null }) => void;

/** The shape stored in each `window.history` entry. */
interface MaskedEntry {
  chessreps: { location: Location; idx: number };
}

export interface MaskedHistory {
  readonly action: Action;
  readonly location: Location;
  createHref(to: To): string;
  createURL(to: To): URL;
  encodeLocation(to: To): { pathname: string; search: string; hash: string };
  push(to: To, state?: unknown): void;
  replace(to: To, state?: unknown): void;
  go(delta: number): void;
  listen(listener: Listener): () => void;
}

function createKey(): string {
  return Math.random().toString(36).slice(2, 10);
}

function readEntry(state: unknown): MaskedEntry['chessreps'] | null {
  if (state && typeof state === 'object' && 'chessreps' in state) {
    return (state as MaskedEntry).chessreps;
  }
  return null;
}

function locationFromWindow(win: Window): Location {
  const { pathname, search, hash } = win.location;
  return { pathname, search, hash, state: null, key: 'default' };
}

export function createMaskedHistory(win: Window = window, maskedUrl = '/'): MaskedHistory {
  const globalHistory = win.history;
  let action: Action = NavigationType.Pop;
  let listener: Listener | null = null;

  const initial = readEntry(globalHistory.state);
  let location: Location = initial?.location ?? locationFromWindow(win);
  let index = initial?.idx ?? 0;
  // Mask the entry we landed on (a deep link, or an older unmasked entry).
  globalHistory.replaceState({ chessreps: { location, idx: index } }, '', maskedUrl);

  function toLocation(to: To, state: unknown): Location {
    const path = typeof to === 'string' ? parsePath(to) : to;
    return {
      pathname: location.pathname,
      search: '',
      hash: '',
      ...path,
      state: state ?? null,
      key: createKey(),
    };
  }

  function createURL(to: To): URL {
    return new URL(typeof to === 'string' ? to : createPath(to), win.location.origin);
  }

  win.addEventListener('popstate', (event: PopStateEvent) => {
    const entry = readEntry(event.state);
    let delta: number | null = null;
    if (entry) {
      delta = entry.idx - index;
      location = entry.location;
      index = entry.idx;
    } else {
      const seen = locationFromWindow(win);
      const maskedPath = parsePath(maskedUrl).pathname ?? '/';
      if (seen.hash && seen.pathname === maskedPath && !seen.search) {
        // A same-document fragment link (a page's section anchor) on the masked URL:
        // the page is unchanged, so keep the current route and only note the fragment.
        location = { ...location, hash: seen.hash, key: createKey() };
      } else {
        // An entry we did not write (e.g. a hand-edited URL): adopt and mask it.
        location = seen;
      }
      index += 1;
      globalHistory.replaceState({ chessreps: { location, idx: index } }, '', maskedUrl);
    }
    action = NavigationType.Pop;
    listener?.({ action, location, delta });
  });

  return {
    get action() {
      return action;
    },
    get location() {
      return location;
    },
    createHref(to) {
      return typeof to === 'string' ? to : createPath(to);
    },
    createURL,
    encodeLocation(to) {
      const url = createURL(to);
      return { pathname: url.pathname, search: url.search, hash: url.hash };
    },
    push(to, state) {
      action = NavigationType.Push;
      location = toLocation(to, state);
      index += 1;
      globalHistory.pushState({ chessreps: { location, idx: index } }, '', maskedUrl);
      listener?.({ action, location, delta: 1 });
    },
    replace(to, state) {
      action = NavigationType.Replace;
      location = toLocation(to, state);
      globalHistory.replaceState({ chessreps: { location, idx: index } }, '', maskedUrl);
      listener?.({ action, location, delta: 0 });
    },
    go(delta) {
      globalHistory.go(delta);
    },
    listen(fn) {
      listener = fn;
      return () => {
        listener = null;
      };
    },
  };
}
